// Judging a vital sign against the clinical rules the clinical lead configured
// (docs/plans/phase1-triage.md §2.3, §4, §5.3–5.4).
//
// The logic is the shared `clinical-rules` module (also used for lab analytes); this file
// is its vital-sign face: rules keyed by `vitalKey`, contexts from VITAL_SIGNS, and the
// error codes the triage API and screens already use.

import {
  assessValue,
  checkWithinLimits,
  CLINICAL_RULE_APPROVALS,
  CLINICAL_RULE_KINDS,
  interpretValue,
  isCritical,
  RULE_INTERPRETATIONS,
  RULE_ISSUES,
  selectClinicalRule,
  validateClinicalRules,
  type ClinicalRuleApproval,
  type ClinicalRuleCore,
  type ClinicalRuleKind,
  type RuleInterpretation,
  type RuleIssue,
  type RuleIssueCode,
} from '../clinical-rules/rules.ts';
import type { Result } from '../result.ts';
import { err, ok } from '../result.ts';
import { isVitalContextAllowed, isVitalKey, type VitalKey } from './definitions.ts';

export { CLINICAL_RULE_APPROVALS, RULE_ISSUES };
export type { ClinicalRuleApproval, RuleIssue, RuleIssueCode };

export const VITAL_RULE_KINDS = CLINICAL_RULE_KINDS;
export type VitalRuleKind = ClinicalRuleKind;

export const OBSERVATION_INTERPRETATIONS = RULE_INTERPRETATIONS;
export type ObservationInterpretation = RuleInterpretation;

export interface VitalRule extends ClinicalRuleCore {
  readonly vitalKey: VitalKey;
}

const subjectOf = (rule: VitalRule): string => rule.vitalKey;

export interface RuleQuery {
  readonly vitalKey: VitalKey;
  readonly kind: VitalRuleKind;
  /** Age in whole days at the clinical time; null when no date of birth is recorded. */
  readonly ageDays: number | null;
  readonly context: string | null;
}

export type RuleSelectionErrorCode = 'vital_rule.overlap';

/**
 * The one rule of a kind that applies to this vital, age and context, or null when none
 * does ("No range set"). A rule for the value's own context wins over one for any context.
 */
export function selectRule(
  rules: readonly VitalRule[],
  query: RuleQuery,
): Result<VitalRule | null, RuleSelectionErrorCode> {
  const selected = selectClinicalRule(
    rules,
    { subject: query.vitalKey, kind: query.kind, ageDays: query.ageDays, context: query.context },
    subjectOf,
  );
  return selected.ok ? ok(selected.value) : err('vital_rule.overlap');
}

export type PossibleCheckErrorCode = 'vital.not_possible';

export interface NotPossible {
  readonly ok: false;
  readonly code: PossibleCheckErrorCode;
  readonly rule: VitalRule;
  readonly side: 'low' | 'high';
}

/** Refuses a value outside the possible limits (CLAUDE.md clinical safety rules). */
export function checkPossible(
  scaled: number,
  limits: VitalRule | null,
): { readonly ok: true } | NotPossible {
  const checked = checkWithinLimits(scaled, limits);
  if (checked.ok) return checked;
  return { ok: false, code: 'vital.not_possible', rule: checked.rule, side: checked.side };
}

export interface Interpretation {
  readonly interpretation: ObservationInterpretation;
  /** The rule that decided the interpretation: the critical rule if critical, else the reference rule. */
  readonly rule: VitalRule | null;
}

/** Flags a possible value: critical first, then the reference range. */
export function interpretVital(
  scaled: number,
  reference: VitalRule | null,
  critical: VitalRule | null,
): Interpretation {
  return interpretValue(scaled, reference, critical);
}

export function isCriticalInterpretation(value: ObservationInterpretation): boolean {
  return isCritical(value);
}

export type VitalAssessmentErrorCode = PossibleCheckErrorCode | RuleSelectionErrorCode;

export interface VitalAssessment {
  readonly interpretation: ObservationInterpretation;
  readonly rule: VitalRule | null;
  /** True when the rule that decided the flag is not yet approved. */
  readonly provisional: boolean;
}

/** The whole judgement for one value: refused if not possible, otherwise flagged. */
export function assessVital(
  rules: readonly VitalRule[],
  input: { vitalKey: VitalKey; scaled: number; ageDays: number | null; context: string | null },
):
  | { readonly ok: true; readonly value: VitalAssessment }
  | NotPossible
  | { readonly ok: false; readonly code: RuleSelectionErrorCode } {
  const assessed = assessValue(
    rules,
    {
      subject: input.vitalKey,
      scaled: input.scaled,
      ageDays: input.ageDays,
      context: input.context,
    },
    subjectOf,
  );
  if (assessed.ok) return assessed;
  if (assessed.code === 'clinical_rule.not_possible' && 'rule' in assessed) {
    return { ok: false, code: 'vital.not_possible', rule: assessed.rule, side: assessed.side };
  }
  return { ok: false, code: 'vital_rule.overlap' };
}

/** Every problem with a set of vital rules, before it is saved. */
export function validateRuleSet(rules: readonly VitalRule[]): RuleIssue[] {
  return validateClinicalRules(rules, {
    subjectOf,
    contextAllowed: (subject, context) =>
      isVitalKey(subject) && isVitalContextAllowed(subject, context),
  });
}
